import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';

import { dedupedRequest } from '../../utils/axios';

type Client = {
  id: number;
  name: string;
};

type LegalArea = {
  id: number;
  name: string;
  color: string;
};

type ChangeRadarEntry = {
  id: number;
  anstehende_aenderungen: string;
  materie: string;
  legal_area_id?: number | null;
  legal_area_name?: string | null;
  legal_area_color?: string | null;
  client_id: number;
  client_name?: string;
  bewertungsstatus: string;
  status_neu: string;
};

const ChangeRadarPanel = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [legalAreas, setLegalAreas] = useState<LegalArea[]>([]);
  const [entries, setEntries] = useState<ChangeRadarEntry[]>([]);
  const [anstehendeAenderungen, setAnstehendeAenderungen] = useState('');
  const [selectedLegalAreaId, setSelectedLegalAreaId] = useState<number | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [clients]
  );

  const sortedLegalAreas = useMemo(
    () => [...legalAreas].sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [legalAreas]
  );

  const loadData = async () => {
    setError(null);
    try {
      const [clientsRes, legalAreasRes, entriesRes] = await Promise.all([
        dedupedRequest<Client[]>({ method: 'GET', url: '/admin/clients' }, { useCache: false }),
        dedupedRequest<LegalArea[]>({ method: 'GET', url: '/admin/legal_areas' }, { useCache: false }),
        dedupedRequest<ChangeRadarEntry[]>({ method: 'GET', url: '/admin/change-radar' }, { useCache: false })
      ]);
      setClients(clientsRes.data || []);
      setLegalAreas(legalAreasRes.data || []);
      setEntries(entriesRes.data || []);
    } catch {
      setError('Änderungsradar-Daten konnten nicht geladen werden.');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleClientSelection = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const values = Array.from(event.target.selectedOptions).map((option) => Number(option.value));
    setSelectedClientIds(values);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!anstehendeAenderungen.trim() || selectedLegalAreaId == null || selectedClientIds.length === 0) {
      setError('Bitte Anstehende Änderungen, Rechtsgebiet und mindestens einen Kunden angeben.');
      return;
    }

    if (anstehendeAenderungen.trim().length > 200) {
      setError('Anstehende Änderungen darf maximal 200 Zeichen lang sein.')
      return;
    }

    try {
      await dedupedRequest<ChangeRadarEntry[]>(
        {
          method: 'POST',
          url: '/admin/change-radar',
          data: {
            anstehende_aenderungen: anstehendeAenderungen.trim(),
            legal_area_id: selectedLegalAreaId,
            client_ids: selectedClientIds
          }
        },
        { useCache: false }
      );

      setAnstehendeAenderungen('');
      setSelectedLegalAreaId(null);
      setSelectedClientIds([]);
      await loadData();
    } catch {
      setError('Der Eintrag konnte nicht gespeichert werden.');
    }
  };

  const handleDelete = async (entryId: number) => {
    setError(null);
    try {
      await dedupedRequest(
        {
          method: 'DELETE',
          url: `/admin/change-radar/${entryId}`
        },
        { useCache: false }
      );
      await loadData();
    } catch {
      setError('Der Eintrag konnte nicht gelöscht werden.');
    }
  };

  return (
    <>
      <h5>Änderungsradar</h5>

      {error && <Alert variant="warning">{error}</Alert>}

      <Form onSubmit={handleSubmit} className="mb-4">
        <Form.Group className="mb-2">
          <Form.Label>Anstehende Änderungen</Form.Label>
          <Form.Control
            value={anstehendeAenderungen}
            onChange={(event) => setAnstehendeAenderungen(event.target.value)}
            placeholder="Text eingeben"
            maxLength={200}
            required
          />
          <Form.Text muted>{anstehendeAenderungen.length}/200</Form.Text>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>Rechtsgebiet</Form.Label>
          <Form.Select
            value={selectedLegalAreaId == null ? '' : String(selectedLegalAreaId)}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedLegalAreaId(value ? Number(value) : null);
            }}
            required
          >
            <option value="">Bitte auswählen</option>
            {sortedLegalAreas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Kunde (Mehrfachauswahl)</Form.Label>
          <Form.Select multiple value={selectedClientIds.map(String)} onChange={handleClientSelection} required>
            {sortedClients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Button type="submit">Änderung zuweisen</Button>
      </Form>

      <Table striped bordered size="sm">
        <thead>
          <tr>
            <th>Anstehende Änderungen</th>
            <th>Rechtsgebiet</th>
            <th>Kunde</th>
            <th>Bewertungsstatus</th>
            <th>Status neu</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td>{entry.anstehende_aenderungen}</td>
              <td>{entry.legal_area_name || entry.materie}</td>
              <td>{entry.client_name || entry.client_id}</td>
              <td>{entry.bewertungsstatus}</td>
              <td>{entry.status_neu}</td>
              <td>
                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(entry.id)}>
                  Löschen
                </Button>
              </td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <td colSpan={6}>Keine Einträge vorhanden.</td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
};

export default ChangeRadarPanel;
